import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/** Cap the permissions round-trip so a cold-start / slow backend never
 * leaves the user stuck on the "Checking permissions…" splash. If the
 * request takes longer than this, we abort and either auto-retry
 * (with backoff, up to MAX_AUTO_ATTEMPTS) or surface a manual retry
 * button. 30s (was 12s) — production Mongo stalls of 7-20s during
 * heavy background jobs were tripping the old cap for every user. */
const PERMISSIONS_TIMEOUT_MS = 30000;
/** Total automatic attempts (first try + auto-retries) before we give
 * up and fall back to empty permissions (which routes to /my-leads). */
const MAX_AUTO_ATTEMPTS = 3;

export default function ProtectedRoute({ roles, page }) {
  const { user } = useAuth();
  const [pagePerms, setPagePerms] = useState(null); // null = loading, {} = empty/fallback
  const [fetchedFor, setFetchedFor] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const [errored, setErrored] = useState(false);
  // Bumped by the global `permissions:updated` event so any live role
  // change (admin grants/revokes a page perm while this tab is open)
  // triggers a fresh /permissions/effective fetch — no reload required.
  // Kept as a separate counter (not `attempt`) so it never collides with
  // the auto-retry-once logic on cold-start failures.
  const [refreshTick, setRefreshTick] = useState(0);
  const pageKey = Array.isArray(page) ? page.join(",") : (page || "");

  // ── Live permissions listener ────────────────────────────────────
  // AuthContext polls `/api/auth/me/permissions-version` and dispatches
  // `permissions:updated` whenever the server-side hash for THIS user
  // changes. We refetch page permissions on that signal so the guard
  // reflects the new grants/revokes within one poll cycle. Also listen
  // for `permissions:probe` (fired on any 403) so if the user tried to
  // do something they no longer have access to, we recompute the guard
  // state immediately for the next render.
  useEffect(() => {
    if (!user) return undefined;
    const bump = () => setRefreshTick((t) => t + 1);
    window.addEventListener("permissions:updated", bump);
    window.addEventListener("permissions:probe", bump);
    return () => {
      window.removeEventListener("permissions:updated", bump);
      window.removeEventListener("permissions:probe", bump);
    };
  }, [user?.email]);

  useEffect(() => {
    if (!user) return;
    if (!pageKey) { setPagePerms({}); setFetchedFor(""); return; }
    let cancelled = false;
    let timeoutId;
    const controller = new AbortController();
    setTimedOut(false);
    // Reset the error flag whenever a fresh attempt kicks off — the
    // splash falls back to the neutral "Checking permissions…" while
    // this attempt is in flight and only flips to the retry UI if THIS
    // attempt also fails.
    setErrored(false);
    (async () => {
      timeoutId = setTimeout(() => {
        controller.abort();
        if (!cancelled) setTimedOut(true);
      }, PERMISSIONS_TIMEOUT_MS);
      try {
        const { data } = await api.get("/permissions/effective", { signal: controller.signal });
        if (!cancelled) { setPagePerms(data.page_permissions || {}); setFetchedFor(pageKey); }
      } catch (e) {
        // Only bail on the cleanup abort (unmount / deps change). A
        // TIMEOUT abort also surfaces as ERR_CANCELED — pre-2026-08-05
        // that early return left `pagePerms` null forever, stranding
        // every user on the splash whenever the backend stalled >12s.
        if (cancelled) return;
        // Show the "server is starting up" message + Retry button
        // straight away — the user should never see a blank splash
        // while we silently retry in the background.
        setErrored(true);
        // Auto-retry with backoff — covers Emergent cold-starts AND
        // transient production DB stalls. The retry UI stays visible
        // during silent re-attempts so the user can hit Retry too.
        if (attempt < MAX_AUTO_ATTEMPTS - 1) {
          setTimeout(() => { if (!cancelled) setAttempt(attempt + 1); }, 1500 * (attempt + 1));
          return;
        }
        setPagePerms({});
        setFetchedFor(pageKey);
      } finally {
        clearTimeout(timeoutId);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
    // `refreshTick` is included so a live permissions:updated event
    // reruns this effect and re-hits /permissions/effective — that's
    // how role changes propagate to the guard without a reload.
  }, [user?.email, pageKey, attempt, refreshTick]);

  const retry = () => {
    setPagePerms(null);
    setFetchedFor("");
    setTimedOut(false);
    setErrored(false);
    setAttempt((a) => a + 1);
  };

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="meta-label">Authenticating…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  if (page) {
    // While a live refresh is in flight because of a live permissions
    // event, `fetchedFor` still points to the previous pageKey — keep
    // serving the last-known outcome instead of flashing the splash.
    // Only fall back to the splash on the very first load or a genuine
    // route change (pageKey mismatch).
    //
    // NOTE: use a strict inequality (not `fetchedFor && …`) — when the
    // previous page was a permission-less route like `/my-leads`,
    // `fetchedFor` is the empty string, and the `&&` short-circuit
    // would silently declare "no route change" the next time the user
    // navigates to a gated page like `/admin`. That left `pagePerms`
    // as the stale `{}` fallback, `anyGranted` fell through to false,
    // and the user got bounced right back to `/my-leads` — reproducing
    // the "can't see admin tabs" report from 2026-07-13.
    const isInitialLoad = pagePerms === null;
    const routeChanged = fetchedFor !== pageKey;
    if (isInitialLoad || routeChanged) {
      const showRetry = errored || timedOut;
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center">
          <div className="meta-label">Checking permissions…</div>
          {showRetry && (
            <>
              <div className="text-xs text-slate-500 max-w-md" data-testid="perms-retry-message">
                The server is starting up. This should only take a moment.
              </div>
              <button
                onClick={retry}
                className="text-xs px-3 py-1 rounded-sm bg-slate-900 text-white hover:bg-slate-800"
                data-testid="perms-retry-btn"
              >
                Retry
              </button>
            </>
          )}
        </div>
      );
    }
    const required = Array.isArray(page) ? page : [page];
    const anyGranted = required.some((p) => pagePerms[p] === true);
    if (!anyGranted) {
      return <Navigate to="/my-leads" replace />;
    }
  }
  return <Outlet />;
}
